/**
 * MyAccessPage — Rich visual dashboard for role/scope/lens/entity access (DRR-102).
 *
 * Redesigned following FERP primitive page patterns:
 *   - TaskTemplatesPage: Tabulator tables with shared config helpers
 *   - HierarchyDiagramView: React Flow for access relationship graph
 *   - ScopeHierarchyPage: ScopeDAGNavigator for org position
 *
 * Tab layout with 5 perspectives:
 *   1. Overview      — Identity card + stat cards + React Flow access graph
 *   2. Scopes        — Tabulator table of all resolved scopes (sortable, filterable)
 *   3. Assignments   — Tabulator table of entity assignments + correlations
 *   4. Org Position  — ScopeDAGNavigator (Cytoscape.js) with breadcrumb
 *   5. Navigation    — Tabulator tables of accessible pages/routes/entity types
 *
 * Route: /my-access (all roles)
 * Uses existing hooks: no new backend queries needed.
 */

import { Tabulator, type ColumnDefinition } from '@/types/tabulator';
import 'tabulator-tables/dist/css/tabulator.min.css';
import '@/styles/tabulator-mantine.css';
import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import {
  Alert,
  Badge,
  Card,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  Center,
  RingProgress,
  Box,
} from '@mantine/core';
import {
  IconCheck,
  IconEye,
  IconLock,
  IconNavigation,
  IconNetwork,
  IconRoute,
  IconShieldCheck,
  IconUsers,
  IconLayoutDashboard,
  IconAlertCircle,
  IconBug,
} from '@tabler/icons-react';

// Recharts for scope distribution charts
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts';

// Framer Motion for animations
import { motion, AnimatePresence } from 'framer-motion';

import { useAuth, useViewLayer } from '@/auth/AuthContext';
import {
  ScopeDAGNavigator,
  ScopeBreadcrumbNavigator,
  useLensEvaluation,
  useAssignedEntityIds,
  useScopeGraph,
} from '@ferp/workflow-forms/scope';
import {
  ViewLayer as GqlViewLayer,
  useGetAllowedNavigationQuery,
  useResolveRoleScopesForAccessQuery,
  useMyRoleIdsQuery,
} from '@/generated/graphql';
import { useDevActorId } from '@/hooks/useDevActorId';
import {
  TABULATOR_DEFAULTS,
  textColumn,
  badgeColumn,
} from '@/utils/tabulatorConfig';
import { AuthTraceTab } from '@/components/scope/AuthTraceTab';

// React Flow for access relationship graph
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  MarkerType,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ── Scope resolution (reuse DevPanel's query) ──────────────────────────────

import { useQuery } from '@apollo/client/react';

// ── Helpers ─────────────────────────────────────────────────────────────────

const SCOPE_CATEGORY_MAP: Record<string, string> = {
  scope: 'Scope Mgmt',
  facility: 'Facility',
  staff: 'Staff',
  care_plan: 'Care Plans',
  approval: 'Approvals',
  billing: 'Billing',
  claims: 'Claims',
  fhir: 'FHIR/Clinical',
  forms: 'Forms',
  task: 'Tasks',
  workflow: 'Workflows',
  incident: 'Incidents',
  medication: 'Medication',
  clinical: 'Clinical',
  accounting: 'Accounting',
  regulatory: 'Regulatory',
  tenant: 'Tenant Admin',
};

function getScopeCategory(scope: string): string {
  const prefix = scope.split(':')[0] ?? 'other';
  return SCOPE_CATEGORY_MAP[prefix] ?? prefix;
}

function getScopeAction(scope: string): string {
  if (scope === '*') return 'wildcard';
  const parts = scope.split(':');
  return parts[parts.length - 1] ?? 'unknown';
}

function scopeColorClass(scope: string): string {
  if (scope === '*') return 'critical';
  if (scope.includes('admin') || scope.includes('manage')) return 'urgent';
  if (scope.includes('write') || scope.includes('create') || scope.includes('approve')) return 'high';
  if (scope.includes('read') || scope.includes('list')) return 'normal';
  return 'low';
}

function scopeBadgeColor(scope: string): string {
  if (scope === '*') return 'red';
  if (scope.includes('admin') || scope.includes('manage')) return 'violet';
  if (scope.includes('write') || scope.includes('create') || scope.includes('approve')) return 'orange';
  if (scope.includes('read') || scope.includes('list')) return 'blue';
  return 'gray';
}

function getScopeLevel(scope: string): string {
  if (scope === '*') return 'wildcard';
  const action = getScopeAction(scope);
  if (action === 'admin' || action === 'manage' || action === 'delete') return 'admin';
  if (action === 'write' || action === 'create' || action === 'update' || action === 'approve') return 'write';
  if (action === 'read' || action === 'list' || action === 'view') return 'read';
  return 'other';
}

// ── Row types for Tabulator tables ──────────────────────────────────────────

interface ScopeRow {
  scope: string;
  category: string;
  action: string;
  level: string;
}

interface AssignmentRow {
  id: string;
  type: string;
  actorId: string;
  direction: string;
}

interface NavItemRow {
  id: string;
  label: string;
  path: string;
  icon: string;
  layer: string;
  sortOrder: number;
}

interface RouteRow {
  path: string;
  component: string;
  layer: string;
  title: string;
  permissions: string;
  lifeCritical: string;
}

interface EntityTypeRow {
  name: string;
  pluralName: string;
  description: string;
  graphqlType: string;
}

// ── Stat Card ───────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  color,
  icon,
  loading,
}: {
  label: string;
  value: string | number;
  color: string;
  icon: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" align="flex-start">
        <div>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">
            {label}
          </Text>
          {loading ? (
            <Loader size="sm" mt="xs" />
          ) : (
            <Text size="xl" fw={700} mt={4}>
              {value}
            </Text>
          )}
        </div>
        <ThemeIcon size="lg" color={color} variant="light" radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
}

// ── Scope Ring Chart ────────────────────────────────────────────────────────

function ScopeRingChart({ scopes }: { scopes: string[] }) {
  const counts = useMemo(() => {
    let read = 0, write = 0, admin = 0, other = 0;
    for (const s of scopes) {
      const level = getScopeLevel(s);
      if (level === 'read') read++;
      else if (level === 'write') write++;
      else if (level === 'admin' || level === 'wildcard') admin++;
      else other++;
    }
    return { read, write, admin, other };
  }, [scopes]);

  const total = scopes.length || 1;

  return (
    <Group gap="md" align="center">
      <RingProgress
        size={100}
        thickness={12}
        roundCaps
        sections={[
          { value: (counts.read / total) * 100, color: 'blue' },
          { value: (counts.write / total) * 100, color: 'orange' },
          { value: (counts.admin / total) * 100, color: 'red' },
          { value: (counts.other / total) * 100, color: 'gray' },
        ]}
        label={<Center><Text fw={700} size="md">{scopes.length}</Text></Center>}
      />
      <Stack gap={2}>
        <Group gap="xs"><Badge size="xs" color="blue" circle /> <Text size="xs">Read ({counts.read})</Text></Group>
        <Group gap="xs"><Badge size="xs" color="orange" circle /> <Text size="xs">Write ({counts.write})</Text></Group>
        <Group gap="xs"><Badge size="xs" color="red" circle /> <Text size="xs">Admin ({counts.admin})</Text></Group>
        {counts.other > 0 && (
          <Group gap="xs"><Badge size="xs" color="gray" circle /> <Text size="xs">Other ({counts.other})</Text></Group>
        )}
      </Stack>
    </Group>
  );
}

// ── Framer Motion variants ──────────────────────────────────────────────

import type { Variants } from 'framer-motion';

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] } },
};

const fadeIn: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.3 } },
};

// ── Recharts: Scope Distribution Bar Chart ──────────────────────────────

const CATEGORY_COLORS = [
  '#228be6', '#be4bdb', '#fab005', '#40c057', '#fd7e14',
  '#e64980', '#15aabf', '#7950f2', '#82c91e', '#f06595',
  '#4c6ef5', '#868e96', '#ff6b6b', '#51cf66', '#fcc419',
  '#339af0', '#c084fc',
];

function ScopeDistributionChart({ scopesByCategory }: { scopesByCategory: [string, string[]][] }) {
  const chartData = useMemo(
    () =>
      scopesByCategory.map(([category, scopes]) => ({
        category,
        count: scopes.length,
        read: scopes.filter((s) => getScopeLevel(s) === 'read').length,
        write: scopes.filter((s) => getScopeLevel(s) === 'write').length,
        admin: scopes.filter((s) => getScopeLevel(s) === 'admin' || getScopeLevel(s) === 'wildcard').length,
      })),
    [scopesByCategory],
  );

  if (chartData.length === 0) return null;

  return (
    <Paper p="md" withBorder radius="md">
      <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="sm">
        Scope Distribution by Category
      </Text>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} margin={{ top: 4, right: 20, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--mantine-color-default-border)" />
          <XAxis
            dataKey="category"
            tick={{ fontSize: 11 }}
            angle={-35}
            textAnchor="end"
            height={60}
          />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <ChartTooltip
            contentStyle={{
              borderRadius: 8,
              fontSize: 12,
              border: '1px solid var(--mantine-color-default-border)',
            }}
          />
          <Bar dataKey="read" stackId="a" fill="#228be6" name="Read" radius={[0, 0, 0, 0]} />
          <Bar dataKey="write" stackId="a" fill="#fd7e14" name="Write" radius={[0, 0, 0, 0]} />
          <Bar dataKey="admin" stackId="a" fill="#fa5252" name="Admin" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}

// ── Recharts: Access Level Pie Chart ────────────────────────────────────

function AccessLevelPieChart({ scopes }: { scopes: string[] }) {
  const pieData = useMemo(() => {
    let read = 0, write = 0, admin = 0, other = 0;
    for (const s of scopes) {
      const level = getScopeLevel(s);
      if (level === 'read') read++;
      else if (level === 'write') write++;
      else if (level === 'admin' || level === 'wildcard') admin++;
      else other++;
    }
    return [
      { name: 'Read', value: read, color: '#228be6' },
      { name: 'Write', value: write, color: '#fd7e14' },
      { name: 'Admin', value: admin, color: '#fa5252' },
      ...(other > 0 ? [{ name: 'Other', value: other, color: '#868e96' }] : []),
    ].filter((d) => d.value > 0);
  }, [scopes]);

  if (pieData.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
          data={pieData}
          cx="50%"
          cy="50%"
          innerRadius={45}
          outerRadius={75}
          paddingAngle={3}
          dataKey="value"
          label={({ name, value }) => `${name}: ${value}`}
        >
          {pieData.map((entry, idx) => (
            <Cell key={`cell-${idx}`} fill={entry.color} />
          ))}
        </Pie>
        <ChartTooltip
          contentStyle={{
            borderRadius: 8,
            fontSize: 12,
            border: '1px solid var(--mantine-color-default-border)',
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── React Flow: Access Relationship Graph ───────────────────────────────────

const NODE_COLORS = {
  actor: '#228be6',
  role: '#be4bdb',
  category: '#fab005',
  assignment: '#40c057',
  lens: '#fd7e14',
};

/**
 * Custom node component for the access relationship graph.
 * Renders styled nodes based on type: actor, role, category, assignment, lens.
 */
function AccessNode({ data }: { data: { label: string; sublabel?: string; type: string; count?: number; color: string } }) {
  return (
    <Box
      style={{
        padding: data.type === 'actor' ? '14px 20px' : '10px 16px',
        borderRadius: data.type === 'actor' ? '50%' : '8px',
        border: `2px solid ${data.color}`,
        backgroundColor: 'white',
        minWidth: data.type === 'actor' ? 100 : 120,
        textAlign: 'center',
        boxShadow: data.type === 'actor'
          ? `0 4px 16px ${data.color}40`
          : '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
      }}
    >
      {/* React Flow handles — required for edge connections */}
      <Handle type="source" position={Position.Right} style={{ opacity: 0, width: 6, height: 6 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0, width: 6, height: 6 }} />
      <Text size={data.type === 'actor' ? 'sm' : 'xs'} fw={700} style={{ color: data.color }}>
        {data.label}
      </Text>
      {data.sublabel && (
        <Text size="xs" c="dimmed" mt={2}>
          {data.sublabel}
        </Text>
      )}
      {data.count !== undefined && data.count > 0 && (
        <Badge size="xs" color={data.color} variant="light" mt={4}>
          {data.count} scopes
        </Badge>
      )}
    </Box>
  );
}

const accessNodeTypes = { accessNode: AccessNode };

function AccessRelationshipGraph({
  userName,
  roleIds,
  scopesByCategory,
  assignedActorIds,
  activeLenses,
  isAdminOrArchitect,
}: {
  userName: string;
  roleIds: string[];
  scopesByCategory: [string, string[]][];
  assignedActorIds: string[] | null;
  activeLenses: Array<{ lensId: string; name: string; phase: string; safetyLevel: string }>;
  isAdminOrArchitect: boolean;
}) {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Center: Actor node
    const actorNodeId = 'actor';
    nodes.push({
      id: actorNodeId,
      type: 'accessNode',
      position: { x: 400, y: 0 },
      data: {
        label: userName || 'Current User',
        sublabel: isAdminOrArchitect ? 'Admin/Architect' : 'Staff',
        type: 'actor',
        color: NODE_COLORS.actor,
      },
    });

    // Role nodes (left side)
    roleIds.forEach((roleId, idx) => {
      const nodeId = `role-${roleId}`;
      const ySpacing = roleIds.length > 1 ? (idx - (roleIds.length - 1) / 2) * 100 : 0;
      nodes.push({
        id: nodeId,
        type: 'accessNode',
        position: { x: 100, y: 140 + ySpacing },
        data: {
          label: roleId,
          sublabel: 'Role Config',
          type: 'role',
          color: NODE_COLORS.role,
        },
      });
      edges.push({
        id: `e-actor-${nodeId}`,
        source: actorNodeId,
        target: nodeId,
        label: 'has role',
        type: 'smoothstep',
        style: { stroke: NODE_COLORS.role, strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: NODE_COLORS.role },
        animated: true,
      });
    });

    // Scope category nodes (right side, below actor)
    const catStartY = 140;
    const catSpacing = Math.min(80, 600 / Math.max(scopesByCategory.length, 1));
    scopesByCategory.forEach(([category, scopes], idx) => {
      const nodeId = `cat-${category.replace(/\s/g, '-')}`;
      nodes.push({
        id: nodeId,
        type: 'accessNode',
        position: { x: 650, y: catStartY + idx * catSpacing },
        data: {
          label: category,
          count: scopes.length,
          type: 'category',
          color: NODE_COLORS.category,
        },
      });
      // Connect categories to roles (if single role, direct; if multiple, from each)
      if (roleIds.length > 0) {
        for (const roleId of roleIds) {
          edges.push({
            id: `e-role-${roleId}-${nodeId}`,
            source: `role-${roleId}`,
            target: nodeId,
            type: 'smoothstep',
            style: { stroke: NODE_COLORS.category, strokeWidth: 1, opacity: 0.5 },
            markerEnd: { type: MarkerType.ArrowClosed, color: NODE_COLORS.category },
          });
        }
      } else {
        edges.push({
          id: `e-actor-${nodeId}`,
          source: actorNodeId,
          target: nodeId,
          type: 'smoothstep',
          style: { stroke: NODE_COLORS.category, strokeWidth: 1, opacity: 0.5 },
          markerEnd: { type: MarkerType.ArrowClosed, color: NODE_COLORS.category },
        });
      }
    });

    // Assignment nodes (below actor, left-center)
    const assignments = assignedActorIds ?? [];
    const displayAssignments = assignments.slice(0, 6); // Show max 6 nodes to avoid clutter
    const remainingCount = assignments.length - displayAssignments.length;
    displayAssignments.forEach((actorId, idx) => {
      const nodeId = `assign-${actorId}`;
      const xOffset = (idx % 3) * 160;
      const yOffset = Math.floor(idx / 3) * 80;
      nodes.push({
        id: nodeId,
        type: 'accessNode',
        position: { x: 180 + xOffset, y: catStartY + scopesByCategory.length * catSpacing + 40 + yOffset },
        data: {
          label: actorId,
          sublabel: 'Assigned Entity',
          type: 'assignment',
          color: NODE_COLORS.assignment,
        },
      });
      edges.push({
        id: `e-actor-${nodeId}`,
        source: actorNodeId,
        target: nodeId,
        label: 'assigned to',
        type: 'smoothstep',
        style: { stroke: NODE_COLORS.assignment, strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: NODE_COLORS.assignment },
      });
    });

    // "+N more" node if truncated
    if (remainingCount > 0) {
      const moreNodeId = 'assign-more';
      nodes.push({
        id: moreNodeId,
        type: 'accessNode',
        position: { x: 500, y: catStartY + scopesByCategory.length * catSpacing + 40 },
        data: {
          label: `+${remainingCount} more`,
          sublabel: 'assignments',
          type: 'assignment',
          color: NODE_COLORS.assignment,
        },
      });
    }

    // Lens nodes (top-right)
    activeLenses.forEach((lens, idx) => {
      const nodeId = `lens-${lens.lensId}`;
      nodes.push({
        id: nodeId,
        type: 'accessNode',
        position: { x: 680 + (idx % 2) * 160, y: -20 + Math.floor(idx / 2) * 70 },
        data: {
          label: lens.name,
          sublabel: `${lens.phase} / ${lens.safetyLevel}`,
          type: 'lens',
          color: NODE_COLORS.lens,
        },
      });
      edges.push({
        id: `e-actor-${nodeId}`,
        source: actorNodeId,
        target: nodeId,
        label: 'filtered by',
        type: 'smoothstep',
        style: { stroke: NODE_COLORS.lens, strokeWidth: 1, strokeDasharray: '5 3' },
        markerEnd: { type: MarkerType.ArrowClosed, color: NODE_COLORS.lens },
      });
    });

    return { nodes, edges };
  }, [userName, roleIds, scopesByCategory, assignedActorIds, activeLenses, isAdminOrArchitect]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  // Re-sync when data changes
  useEffect(() => {
    onNodesChange(initialNodes.map((n) => ({ type: 'reset' as const, item: n })));
    onEdgesChange(initialEdges.map((e) => ({ type: 'reset' as const, item: e })));
  }, [initialNodes, initialEdges, onNodesChange, onEdgesChange]);

  return (
    <Box style={{ height: 500, border: '1px solid var(--mantine-color-default-border)', borderRadius: 8 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={accessNodeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        minZoom={0.3}
        maxZoom={1.5}
      >
        <Background color="#f1f3f5" gap={20} />
        <Controls position="bottom-left" />
        <MiniMap
          nodeStrokeColor={(node: Node) => {
            const type = node.data?.type ?? 'actor';
            return NODE_COLORS[type as keyof typeof NODE_COLORS] ?? '#868e96';
          }}
          nodeColor={(node: Node) => {
            const type = node.data?.type ?? 'actor';
            return `${NODE_COLORS[type as keyof typeof NODE_COLORS] ?? '#868e96'}20`;
          }}
          style={{ borderRadius: 8 }}
        />
        <Panel position="top-left">
          <Paper p="xs" withBorder radius="sm" style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
            <Text size="xs" fw={600} mb={4}>Legend</Text>
            <Stack gap={2}>
              <Group gap={6}><Box w={10} h={10} style={{ borderRadius: '50%', backgroundColor: NODE_COLORS.actor }} /> <Text size="xs">Actor</Text></Group>
              <Group gap={6}><Box w={10} h={10} style={{ borderRadius: 2, backgroundColor: NODE_COLORS.role }} /> <Text size="xs">Role</Text></Group>
              <Group gap={6}><Box w={10} h={10} style={{ borderRadius: 2, backgroundColor: NODE_COLORS.category }} /> <Text size="xs">Scope Category</Text></Group>
              <Group gap={6}><Box w={10} h={10} style={{ borderRadius: 2, backgroundColor: NODE_COLORS.assignment }} /> <Text size="xs">Assignment</Text></Group>
              <Group gap={6}><Box w={10} h={10} style={{ borderRadius: 2, backgroundColor: NODE_COLORS.lens }} /> <Text size="xs">Lens</Text></Group>
            </Stack>
          </Paper>
        </Panel>
      </ReactFlow>
    </Box>
  );
}

// ── Edge Summary ────────────────────────────────────────────────────────────

const EDGE_COLORS: Record<string, string> = {
  CONTAINS: 'gray',
  ASSIGNED_TO: 'green',
  LOCATED_AT: 'yellow',
  CORRELATES: 'orange',
};

function EdgeSummary({ edges }: { edges: Array<{ edgeType: string }> }) {
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const edge of edges) {
      const t = edge.edgeType ?? 'UNKNOWN';
      map[t] = (map[t] ?? 0) + 1;
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [edges]);

  return (
    <Group gap="md">
      {counts.map(([type, count]) => (
        <Tooltip key={type} label={`${count} ${type} edge${count !== 1 ? 's' : ''}`}>
          <Badge size="lg" variant="outline" color={EDGE_COLORS[type] ?? 'gray'} leftSection={
            <Text size="xs" fw={700}>{count}</Text>
          }>
            {type}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

export function MyAccessPage() {
  const { user, tenant } = useAuth();
  const viewLayer = useViewLayer();
  const { actorId: resolvedActorId, actorName } = useDevActorId();

  // Tabulator refs
  const scopeTableRef = useRef<HTMLDivElement>(null);
  const scopeTabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const assignmentTableRef = useRef<HTMLDivElement>(null);
  const assignmentTabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const navTableRef = useRef<HTMLDivElement>(null);
  const navTabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const routeTableRef = useRef<HTMLDivElement>(null);
  const routeTabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const entityTypeTableRef = useRef<HTMLDivElement>(null);
  const entityTypeTabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);

  // Active tab for conditional table init
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  // DAG navigator state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Get role IDs from backend (DRR-232 Track E: wired myRoleIds query)
  const { data: roleIdsData, loading: roleIdsLoading } = useMyRoleIdsQuery();
  const backendRoleIds = roleIdsData?.myRoleIds ?? [];

  // Fallback: dev mode localStorage roles (for impersonation panel)
  const [localRoleIds, setLocalRoleIds] = useState<string[]>([]);
  useEffect(() => {
    const readLocalStorage = () => {
      try {
        const stored = localStorage.getItem('dev_actor_role_ids');
        setLocalRoleIds(stored ? stored.split(',').filter(Boolean) : []);
      } catch { /* ignore */ }
    };
    readLocalStorage();
    window.addEventListener('ui:token-set', readLocalStorage);
    return () => window.removeEventListener('ui:token-set', readLocalStorage);
  }, []);

  // Combine: prefer backend roles, fall back to localStorage for dev
  const roleIds = backendRoleIds.length > 0 ? backendRoleIds : localRoleIds;

  // ── 1. Resolve scopes from backend role configs ──────────────────────────

  const { data: scopeData, loading: scopeLoading } = useResolveRoleScopesForAccessQuery({
    variables: { roleIds },
    skip: roleIds.length === 0,
    fetchPolicy: 'network-only',
  });
  const resolvedScopes = scopeData?.resolveRoleScopes ?? [];

  // Scope rows for Tabulator
  const scopeRows = useMemo<ScopeRow[]>(
    () =>
      resolvedScopes.map((scope) => ({
        scope,
        category: getScopeCategory(scope),
        action: getScopeAction(scope),
        level: scopeColorClass(scope),
      })),
    [resolvedScopes],
  );

  // Group scopes by category for overview + react flow
  const scopesByCategory = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const scope of resolvedScopes) {
      const cat = getScopeCategory(scope);
      (groups[cat] ??= []).push(scope);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [resolvedScopes]);

  // ── 2. Active lenses ─────────────────────────────────────────────────────

  const { activeLenses, fetchActiveLenses } = useLensEvaluation();
  useEffect(() => {
    fetchActiveLenses();
  }, [fetchActiveLenses]);

  // ── 3. Assigned entities ──────────────────────────────────────────────────

  const actorAuthId = user?.id ?? '';
  const isAdminOrArchitect = viewLayer === 'architect' || viewLayer === 'admin';
  const { assignedActorIds, correlatedEntityIds, isLoading: assignedLoading } =
    useAssignedEntityIds({
      actorId: actorAuthId,
      skip: isAdminOrArchitect,
    });

  // Assignment rows for Tabulator
  const assignmentRows = useMemo<AssignmentRow[]>(() => {
    const rows: AssignmentRow[] = [];
    if (assignedActorIds) {
      for (const id of assignedActorIds) {
        rows.push({ id, type: 'ASSIGNED_TO', actorId: id, direction: 'outbound' });
      }
    }
    if (correlatedEntityIds) {
      for (const id of correlatedEntityIds) {
        rows.push({ id, type: 'CORRELATES', actorId: id, direction: 'correlated' });
      }
    }
    return rows;
  }, [assignedActorIds, correlatedEntityIds]);

  // ── 4. Scope graph ───────────────────────────────────────────────────────

  const { graph, isLoading: graphLoading, scopePath, fetchScopePath } = useScopeGraph();

  const handleNodeSelect = useCallback(
    (actorId: string) => {
      setSelectedNodeId(actorId);
      fetchScopePath(actorId);
    },
    [fetchScopePath],
  );

  // ── 5. Allowed navigation ────────────────────────────────────────────────

  const gqlLayer = viewLayer.toUpperCase() as GqlViewLayer;
  const { data: navData, loading: navLoading } = useGetAllowedNavigationQuery({
    variables: { tenantId: tenant?.id ?? '', layer: gqlLayer },
    skip: !tenant?.id,
  });
  const allowedNavItems = navData?.allowedNavigation?.navItems ?? [];
  const allowedRoutes = navData?.allowedNavigation?.routes ?? [];
  const allowedEntityTypes = navData?.allowedNavigation?.allowedEntityTypes ?? [];

  // Nav item rows
  const navItemRows = useMemo<NavItemRow[]>(
    () =>
      allowedNavItems.map((item) => ({
        id: item.id,
        label: item.label,
        path: item.path,
        icon: item.icon,
        layer: item.layer,
        sortOrder: item.sortOrder,
      })),
    [allowedNavItems],
  );

  // Route rows
  const routeRows = useMemo<RouteRow[]>(
    () =>
      allowedRoutes.map((route) => ({
        path: route.path,
        component: route.component,
        layer: route.layer,
        title: route.title,
        permissions: route.requiredPermissions.join(', '),
        lifeCritical: route.requiresLifeCriticalAuth ? 'Yes' : 'No',
      })),
    [allowedRoutes],
  );

  // Entity type rows
  const entityTypeRows = useMemo<EntityTypeRow[]>(
    () =>
      allowedEntityTypes.map((et) => ({
        name: et.name,
        pluralName: et.pluralName,
        description: et.description,
        graphqlType: et.graphqlType,
      })),
    [allowedEntityTypes],
  );

  // ── Tabulator: Scopes table ──────────────────────────────────────────────

  const scopeColumns = useMemo<ColumnDefinition[]>(
    () => [
      textColumn('scope', { title: 'Scope', widthGrow: 3 }),
      textColumn('category', { title: 'Category', width: 160 }),
      textColumn('action', { title: 'Action', width: 130 }),
      badgeColumn('level', {
        critical: 'critical',
        urgent: 'urgent',
        high: 'high',
        normal: 'normal',
        low: 'low',
      }, { title: 'Access Level', width: 130 }),
    ],
    [],
  );

  useEffect(() => {
    if (!scopeTableRef.current || activeTab !== 'scopes') return;
    const instance = new Tabulator(scopeTableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: scopeRows as unknown as Record<string, unknown>[],
      columns: scopeColumns,
      height: '500px',
      placeholder: scopeLoading ? 'Loading scopes...' : 'No scopes resolved for current role',
      initialSort: [{ column: 'category', dir: 'asc' }],
    });
    scopeTabulatorRef.current = instance;
    return () => {
      instance.destroy();
      scopeTabulatorRef.current = null;
    };
  }, [scopeRows, scopeColumns, activeTab, scopeLoading]);

  // ── Tabulator: Assignments table ─────────────────────────────────────────

  const assignmentColumns = useMemo<ColumnDefinition[]>(
    () => [
      textColumn('actorId', { title: 'Actor / Entity ID', widthGrow: 3 }),
      badgeColumn('type', {
        ASSIGNED_TO: 'builtin',
        CORRELATES: 'saved',
      }, { title: 'Edge Type', width: 150 }),
      textColumn('direction', { title: 'Direction', width: 130 }),
    ],
    [],
  );

  useEffect(() => {
    if (!assignmentTableRef.current || activeTab !== 'assignments') return;
    const instance = new Tabulator(assignmentTableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: assignmentRows as unknown as Record<string, unknown>[],
      columns: assignmentColumns,
      height: '500px',
      placeholder: isAdminOrArchitect
        ? 'Admin/Architect bypass: All entities visible'
        : assignedLoading
          ? 'Loading assignments...'
          : 'No scope edge assignments found',
    });
    assignmentTabulatorRef.current = instance;
    return () => {
      instance.destroy();
      assignmentTabulatorRef.current = null;
    };
  }, [assignmentRows, assignmentColumns, activeTab, isAdminOrArchitect, assignedLoading]);

  // ── Tabulator: Navigation items table ────────────────────────────────────

  const navColumns = useMemo<ColumnDefinition[]>(
    () => [
      textColumn('label', { title: 'Page', widthGrow: 2 }),
      textColumn('path', { title: 'Path', widthGrow: 2 }),
      textColumn('icon', { title: 'Icon', width: 120 }),
      textColumn('layer', { title: 'Layer', width: 110 }),
      {
        title: 'Order',
        field: 'sortOrder',
        sorter: 'number',
        width: 80,
        hozAlign: 'center' as const,
      } as ColumnDefinition,
    ],
    [],
  );

  useEffect(() => {
    if (!navTableRef.current || activeTab !== 'navigation') return;
    const instance = new Tabulator(navTableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: navItemRows as unknown as Record<string, unknown>[],
      columns: navColumns,
      height: '350px',
      placeholder: navLoading ? 'Loading navigation...' : 'No accessible navigation items',
      initialSort: [{ column: 'sortOrder', dir: 'asc' }],
    });
    navTabulatorRef.current = instance;
    return () => {
      instance.destroy();
      navTabulatorRef.current = null;
    };
  }, [navItemRows, navColumns, activeTab, navLoading]);

  // ── Tabulator: Routes table ──────────────────────────────────────────────

  const routeColumns = useMemo<ColumnDefinition[]>(
    () => [
      textColumn('title', { title: 'Title', widthGrow: 2 }),
      textColumn('path', { title: 'Route', widthGrow: 2 }),
      textColumn('component', { title: 'Component', width: 180 }),
      textColumn('layer', { title: 'Layer', width: 110 }),
      textColumn('permissions', { title: 'Required Permissions', widthGrow: 2 }),
      badgeColumn('lifeCritical', {
        Yes: 'critical',
        No: 'normal',
      }, { title: 'Life-Critical', width: 120 }),
    ],
    [],
  );

  useEffect(() => {
    if (!routeTableRef.current || activeTab !== 'navigation') return;
    const instance = new Tabulator(routeTableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: routeRows as unknown as Record<string, unknown>[],
      columns: routeColumns,
      height: '350px',
      placeholder: navLoading ? 'Loading routes...' : 'No accessible routes',
      initialSort: [{ column: 'title', dir: 'asc' }],
    });
    routeTabulatorRef.current = instance;
    return () => {
      instance.destroy();
      routeTabulatorRef.current = null;
    };
  }, [routeRows, routeColumns, activeTab, navLoading]);

  // ── Tabulator: Entity types table ────────────────────────────────────────

  const entityTypeColumns = useMemo<ColumnDefinition[]>(
    () => [
      textColumn('name', { title: 'Entity Type', widthGrow: 2 }),
      textColumn('pluralName', { title: 'Plural', width: 160 }),
      textColumn('description', { title: 'Description', widthGrow: 3 }),
      textColumn('graphqlType', { title: 'GraphQL Type', width: 180 }),
    ],
    [],
  );

  useEffect(() => {
    if (!entityTypeTableRef.current || activeTab !== 'navigation') return;
    const instance = new Tabulator(entityTypeTableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: entityTypeRows as unknown as Record<string, unknown>[],
      columns: entityTypeColumns,
      height: '300px',
      placeholder: navLoading ? 'Loading entity types...' : 'No accessible entity types',
      initialSort: [{ column: 'name', dir: 'asc' }],
    });
    entityTypeTabulatorRef.current = instance;
    return () => {
      instance.destroy();
      entityTypeTabulatorRef.current = null;
    };
  }, [entityTypeRows, entityTypeColumns, activeTab, navLoading]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* ── Page Header ──────────────────────────────────────────── */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ThemeIcon size="xl" color="blue" variant="light" radius="md">
              <IconShieldCheck size={24} />
            </ThemeIcon>
            <div>
              <Title order={2}>My Access</Title>
              <Text size="sm" c="dimmed">
                Role-based access, entity assignments, org position, and navigation (DRR-102)
              </Text>
            </div>
          </Group>
          <Group gap="xs">
            <Badge
              size="lg"
              color={
                viewLayer === 'architect' ? 'violet' :
                viewLayer === 'admin' ? 'orange' : 'blue'
              }
              variant="filled"
            >
              {viewLayer}
            </Badge>
            {roleIds.map((id) => (
              <Badge key={id} size="sm" variant="outline" color="gray">{id}</Badge>
            ))}
          </Group>
        </Group>

        {/* ── Stat Cards (animated) ────────────────────────────────── */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="show"
        >
          <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
            <motion.div variants={fadeUp}>
              <StatCard
                label="Scopes"
                value={resolvedScopes.length}
                color="blue"
                icon={<IconShieldCheck size={20} />}
                loading={scopeLoading}
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <StatCard
                label="Lenses"
                value={activeLenses?.length ?? 0}
                color="grape"
                icon={<IconEye size={20} />}
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <StatCard
                label="Assignments"
                value={
                  isAdminOrArchitect
                    ? 'Bypass'
                    : (assignedActorIds?.length ?? 0)
                }
                color="green"
                icon={<IconUsers size={20} />}
                loading={assignedLoading}
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <StatCard
                label="Nav Pages"
                value={allowedNavItems.length}
                color="cyan"
                icon={<IconNavigation size={20} />}
                loading={navLoading}
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <StatCard
                label="Graph Nodes"
                value={graph?.nodeCount ?? 0}
                color="indigo"
                icon={<IconNetwork size={20} />}
                loading={graphLoading}
              />
            </motion.div>
          </SimpleGrid>
        </motion.div>

        {/* ── Tabbed Panels ────────────────────────────────────────── */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconLayoutDashboard size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="scopes" leftSection={<IconLock size={16} />}>
              Scopes
              <Badge size="xs" variant="light" ml={6}>
                {scopeLoading ? '...' : resolvedScopes.length}
              </Badge>
            </Tabs.Tab>
            <Tabs.Tab value="assignments" leftSection={<IconUsers size={16} />}>
              Assignments
              <Badge size="xs" variant="light" ml={6}>
                {isAdminOrArchitect ? '*' : assignedLoading ? '...' : assignmentRows.length}
              </Badge>
            </Tabs.Tab>
            <Tabs.Tab value="org-position" leftSection={<IconNetwork size={16} />}>
              Org Position
            </Tabs.Tab>
            <Tabs.Tab value="navigation" leftSection={<IconRoute size={16} />}>
              Navigation
              <Badge size="xs" variant="light" ml={6}>
                {navLoading ? '...' : allowedNavItems.length}
              </Badge>
            </Tabs.Tab>
            <Tabs.Tab value="auth-trace" leftSection={<IconBug size={16} />}>
              Auth Trace
            </Tabs.Tab>
          </Tabs.List>

          {/* ── Tab 1: Overview ──────────────────────────────────────── */}
          <Tabs.Panel value="overview" pt="md">
            <AnimatePresence mode="wait">
              <motion.div
                key="overview"
                variants={staggerContainer}
                initial="hidden"
                animate="show"
              >
            <Stack gap="md">
              {/* Identity card */}
              <motion.div variants={fadeUp}>
              <Paper p="md" withBorder radius="md">
                <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="xs">
                  Identity
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 5 }}>
                  <div>
                    <Text size="xs" c="dimmed">User</Text>
                    <Text fw={600}>{user?.displayName ?? actorName ?? 'Unknown'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Actor ID</Text>
                    <Text fw={600} ff="monospace" size="sm">{resolvedActorId ?? '—'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Auth ID</Text>
                    <Text fw={600} ff="monospace" size="sm">{actorAuthId || 'None'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Tenant</Text>
                    <Text fw={600}>{tenant?.id ?? 'None'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">View Layer</Text>
                    <Badge
                      color={
                        viewLayer === 'architect' ? 'violet' :
                        viewLayer === 'admin' ? 'orange' : 'blue'
                      }
                      size="lg"
                    >
                      {viewLayer}
                    </Badge>
                  </div>
                </SimpleGrid>
              </Paper>
              </motion.div>

              {/* Recharts: Scope distribution + access level pie */}
              <motion.div variants={fadeUp}>
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                <ScopeDistributionChart scopesByCategory={scopesByCategory} />
                <Paper p="md" withBorder radius="md">
                  <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="sm">
                    Access Level Breakdown
                  </Text>
                  {scopeLoading ? (
                    <Center py="xl"><Loader size="sm" /></Center>
                  ) : resolvedScopes.length === 0 ? (
                    <Center py="xl"><Text size="sm" c="dimmed">No scopes resolved</Text></Center>
                  ) : (
                    <AccessLevelPieChart scopes={resolvedScopes} />
                  )}
                </Paper>
              </SimpleGrid>
              </motion.div>

              {/* React Flow access relationship graph */}
              <motion.div variants={fadeUp}>
              <Paper p="md" withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                      Access Relationship Graph
                    </Text>
                    <Text size="xs" c="dimmed">
                      Visual map: Actor → Roles → Scope Categories → Assignments → Lenses
                    </Text>
                  </div>
                  <ScopeRingChart scopes={resolvedScopes} />
                </Group>
                {scopeLoading ? (
                  <Center py="xl"><Loader /></Center>
                ) : (
                  <AccessRelationshipGraph
                    userName={user?.displayName ?? actorName ?? 'User'}
                    roleIds={roleIds}
                    scopesByCategory={scopesByCategory}
                    assignedActorIds={assignedActorIds}
                    activeLenses={activeLenses ?? []}
                    isAdminOrArchitect={isAdminOrArchitect}
                  />
                )}
              </Paper>
              </motion.div>

              {/* Active lenses + assignment summary side-by-side */}
              <motion.div variants={fadeUp}>
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                <Paper p="md" withBorder radius="md">
                  <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="sm">
                    Active Lenses ({activeLenses?.length ?? 0})
                  </Text>
                  {!activeLenses || activeLenses.length === 0 ? (
                    <Text size="sm" c="dimmed">No active lenses configured.</Text>
                  ) : (
                    <Stack gap="xs">
                      {activeLenses.map((lens) => (
                        <Card key={lens.lensId} padding="sm" withBorder radius="md">
                          <Group justify="space-between" mb={4}>
                            <Text size="sm" fw={600}>{lens.name}</Text>
                            <Badge
                              size="xs"
                              color={
                                lens.safetyLevel === 'CRITICAL' ? 'red' :
                                lens.safetyLevel === 'HIGH' ? 'orange' :
                                lens.safetyLevel === 'MEDIUM' ? 'yellow' : 'green'
                              }
                            >
                              {lens.safetyLevel}
                            </Badge>
                          </Group>
                          <Group gap={4}>
                            <Badge size="xs" variant="outline">{lens.phase}</Badge>
                            {lens.vertical && (
                              <Badge size="xs" variant="outline" color="gray">{lens.vertical}</Badge>
                            )}
                          </Group>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Paper>

                <Paper p="md" withBorder radius="md">
                  <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="sm">
                    Entity Assignment Summary
                  </Text>
                  {isAdminOrArchitect ? (
                    <Alert icon={<IconCheck size={16} />} color="green" title="Full Access">
                      Admin/Architect view layer bypasses entity filtering. All entities visible.
                    </Alert>
                  ) : assignedLoading ? (
                    <Loader size="sm" />
                  ) : assignmentRows.length === 0 ? (
                    <Alert icon={<IconLock size={16} />} color="yellow" title="No Assignments">
                      No ASSIGNED_TO scope edges found. You cannot see filtered entities.
                      Ask an admin to create assignment edges via the Scope Hierarchy page.
                    </Alert>
                  ) : (
                    <Stack gap="md">
                      <SimpleGrid cols={2}>
                        <div>
                          <Text size="xs" c="dimmed">Assigned Actors</Text>
                          <Text fw={700} size="xl">{assignedActorIds?.length ?? 0}</Text>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">Correlated External IDs</Text>
                          <Text fw={700} size="xl">{correlatedEntityIds?.length ?? 0}</Text>
                        </div>
                      </SimpleGrid>
                      {graph && graph.edges?.length > 0 && (
                        <>
                          <Divider />
                          <Text size="xs" fw={600} c="dimmed">Graph Edges</Text>
                          <EdgeSummary edges={graph.edges} />
                        </>
                      )}
                    </Stack>
                  )}
                </Paper>
              </SimpleGrid>
              </motion.div>
            </Stack>
              </motion.div>
            </AnimatePresence>
          </Tabs.Panel>

          {/* ── Tab 2: Scopes (Tabulator) ────────────────────────────── */}
          <Tabs.Panel value="scopes" pt="md">
            <motion.div variants={fadeIn} initial="hidden" animate="show">
            <Stack gap="md">
              <Paper p="md" withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600}>Resolved Scopes</Text>
                    <Text size="xs" c="dimmed">
                      All permissions resolved from role configuration for: {roleIds.join(', ') || '(none)'}
                    </Text>
                  </div>
                  <Badge size="lg" variant="light">
                    {resolvedScopes.length} scopes
                  </Badge>
                </Group>
                {scopeLoading ? (
                  <Stack align="center" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">Resolving permissions...</Text>
                  </Stack>
                ) : (
                  <div ref={scopeTableRef} />
                )}
              </Paper>
            </Stack>
            </motion.div>
          </Tabs.Panel>

          {/* ── Tab 3: Assignments (Tabulator) ───────────────────────── */}
          <Tabs.Panel value="assignments" pt="md">
            <motion.div variants={fadeIn} initial="hidden" animate="show">
            <Stack gap="md">
              <Paper p="md" withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600}>Entity Assignments</Text>
                    <Text size="xs" c="dimmed">
                      ASSIGNED_TO and CORRELATES scope edges determine what entities you can see
                    </Text>
                  </div>
                  {isAdminOrArchitect ? (
                    <Badge size="lg" color="green" variant="light">
                      Admin Bypass
                    </Badge>
                  ) : (
                    <Badge size="lg" variant="light">
                      {assignmentRows.length} edges
                    </Badge>
                  )}
                </Group>

                {isAdminOrArchitect ? (
                  <Alert icon={<IconCheck size={16} />} color="green" title="Full Access (Bypass)">
                    Admin/Architect view layer bypasses entity filtering via skip=true.
                    You see all entities without needing explicit ASSIGNED_TO edges.
                    Switch to a staff role (CNA, LPN) with Ctrl+D to see scoped assignments.
                  </Alert>
                ) : assignedLoading ? (
                  <Stack align="center" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">Loading scope edges...</Text>
                  </Stack>
                ) : (
                  <div ref={assignmentTableRef} />
                )}
              </Paper>
            </Stack>
            </motion.div>
          </Tabs.Panel>

          {/* ── Tab 4: Org Position (DAG Navigator) ──────────────────── */}
          <Tabs.Panel value="org-position" pt="md">
            <motion.div variants={fadeIn} initial="hidden" animate="show">
            <Stack gap="md">
              <Paper p="md" withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600}>Organization Scope Graph</Text>
                    <Text size="xs" c="dimmed">
                      Contains, Assigned To, Located At, and Correlates edges (DRR-075).
                      Click a node to see its scope path.
                    </Text>
                  </div>
                  <Group gap="xs">
                    <Badge size="sm" variant="light" color="gray">
                      {graph?.nodeCount ?? 0} nodes
                    </Badge>
                    <Badge size="sm" variant="light" color="gray">
                      {graph?.edgeCount ?? 0} edges
                    </Badge>
                  </Group>
                </Group>

                {graphLoading ? (
                  <Stack align="center" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">Loading scope graph...</Text>
                  </Stack>
                ) : !graph || !graph.edges?.length ? (
                  <Alert icon={<IconNetwork size={16} />} color="yellow" title="No Graph Data">
                    No scope graph data available. Scope edges define organization hierarchy and assignments.
                  </Alert>
                ) : (
                  <>
                    <ScopeBreadcrumbNavigator
                      selectedNodeId={selectedNodeId}
                      onNodeNavigate={handleNodeSelect}
                    />
                    <ScopeDAGNavigator
                      graph={graph}
                      onNodeSelect={handleNodeSelect}
                      selectedNodeId={selectedNodeId}
                      scopePath={scopePath?.path}
                      height={550}
                      excludeLeaves  // Org graph excludes subjects/residents
                    />

                    {/* Selected node details */}
                    {selectedNodeId && (
                      <Paper p="sm" withBorder radius="md" mt="sm">
                        <SimpleGrid cols={3}>
                          <div>
                            <Text size="xs" c="dimmed">Selected Node</Text>
                            <Text fw={600} ff="monospace">{selectedNodeId}</Text>
                          </div>
                          <div>
                            <Text size="xs" c="dimmed">Path Depth</Text>
                            <Text fw={600}>{scopePath?.depth ?? '—'}</Text>
                          </div>
                          <div>
                            <Text size="xs" c="dimmed">Path</Text>
                            <Group gap={4}>
                              {scopePath?.path?.map((nodeId) => (
                                <Badge
                                  key={nodeId}
                                  size="xs"
                                  variant={nodeId === selectedNodeId ? 'filled' : 'outline'}
                                  color={nodeId === selectedNodeId ? 'blue' : 'gray'}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleNodeSelect(nodeId)}
                                >
                                  {nodeId}
                                </Badge>
                              )) ?? <Text size="xs" c="dimmed">No path</Text>}
                            </Group>
                          </div>
                        </SimpleGrid>
                      </Paper>
                    )}

                    {/* Edge type summary */}
                    <Divider my="sm" />
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="xs">
                      Edge Summary
                    </Text>
                    <EdgeSummary edges={graph.edges} />
                  </>
                )}
              </Paper>
            </Stack>
            </motion.div>
          </Tabs.Panel>

          {/* ── Tab 5: Navigation (Tabulator x3) ─────────────────────── */}
          <Tabs.Panel value="navigation" pt="md">
            <motion.div variants={fadeIn} initial="hidden" animate="show">
            <Stack gap="md">
              {/* Nav items table */}
              <Paper p="md" withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600}>Accessible Pages</Text>
                    <Text size="xs" c="dimmed">
                      Navigation items visible for view layer: {viewLayer}
                    </Text>
                  </div>
                  <Badge size="lg" variant="light">
                    {allowedNavItems.length} pages
                  </Badge>
                </Group>
                {navLoading ? (
                  <Stack align="center" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">Loading navigation config...</Text>
                  </Stack>
                ) : allowedNavItems.length === 0 ? (
                  <Alert icon={<IconLock size={16} />} color="yellow" title="No Navigation">
                    No navigation items are accessible for your current scopes and view layer ({viewLayer}).
                  </Alert>
                ) : (
                  <div ref={navTableRef} />
                )}
              </Paper>

              {/* Routes table */}
              <Paper p="md" withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600}>Accessible Routes</Text>
                    <Text size="xs" c="dimmed">
                      Backend route permissions and life-critical auth requirements
                    </Text>
                  </div>
                  <Badge size="lg" variant="light">
                    {allowedRoutes.length} routes
                  </Badge>
                </Group>
                {navLoading ? (
                  <Loader size="sm" />
                ) : (
                  <div ref={routeTableRef} />
                )}
              </Paper>

              {/* Entity types table */}
              <Paper p="md" withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw={600}>Accessible Entity Types</Text>
                    <Text size="xs" c="dimmed">
                      Entity types visible based on scope permissions (DRR-103)
                    </Text>
                  </div>
                  <Badge size="lg" variant="light">
                    {allowedEntityTypes.length} types
                  </Badge>
                </Group>
                {navLoading ? (
                  <Loader size="sm" />
                ) : (
                  <div ref={entityTypeTableRef} />
                )}
              </Paper>
            </Stack>
            </motion.div>
          </Tabs.Panel>

          {/* ── Tab 6: Auth Trace (DRR-315) ─────────────────────────────── */}
          <Tabs.Panel value="auth-trace" pt="md">
            <AuthTraceTab />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
